import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '@vwfs-bronson/bronson-react';
import { FileUpload, getFileMimeType, parseAndDownloadBase64FileString } from '../../../../components/FileUpload';
import {
  deletePendingContractStipulationSingleDocument,
  getPendingContractStipulationDocuments,
  getPendingContractStipulations,
  getPendingContractStipulationSingleDocument,
  uploadPendingContractStipulationDocuments,
} from '../../../../services/api/miles/miles-services';
import { useNavigate } from '../../../../services/routing/navigate';
import { getBase64 } from '../../../utils';
import { getSubPagePaymentValidationSchema } from '../../validation';

export const DocumentsSection = ({ contractId, formProps, setFieldValue }) => {
  const { t } = useTranslation();
  const [stipulations, setStipulations] = useState<
    MilesPosContractIntegrationService.Components.Schemas.DocumentsContractStipulationsResponse[]
  >([]);
  const [loadingStipulation, setLoadingStipulation] = useState<string>('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const subPagePaymentValidationSchema = useMemo(() => getSubPagePaymentValidationSchema(), [t]);

  const navigate = useNavigate();

  const getStipulations = useCallback(async () => {
    const receivedStipulations = await getPendingContractStipulations(contractId);
    const filteredStipulations =
      receivedStipulations.documents?.filter((document) => document.stipulationStatus !== 'ACTIVE') ?? [];
    setStipulations(filteredStipulations);
  }, [contractId]);

  const getStipulationsDocuments = useCallback(async () => {
    const { documents } = await getPendingContractStipulationDocuments(contractId);

    stipulations.forEach((stipulation) => {
      const stipulationDocs = documents.filter((doc) => doc.stipulationId === stipulation.stipulationId);
      if (stipulationDocs) {
        const docsToRecords = stipulationDocs.map((doc) => ({
          name: doc.description,
          mimeType: getFileMimeType(doc.documentFormat),
          tags: [`stipulation-${doc.stipulationId}`, `document-${doc.documentId}`],
          id: doc.documentId,
        }));
        setFieldValue(`stipulation-${stipulation.stipulationId}`, docsToRecords);
      }
    });
  }, [contractId, stipulations, setFieldValue]);

  const deleteStipulationDocument = useCallback(
    async (stipulationId: string, documentId: string) => {
      setLoadingStipulation(stipulationId);
      try {
        await deletePendingContractStipulationSingleDocument(contractId, stipulationId, documentId);
        const newFieldValues =
          formProps.values[`stipulation-${stipulationId}`]?.filter((record) => record.id !== documentId) || [];
        setFieldValue(`stipulation-${stipulationId}`, newFieldValues);
        setLoadingStipulation('');
      } catch (error) {
        setLoadingStipulation('');
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },
    [contractId, formProps.values, setFieldValue]
  );

  const downloadStipulationDocument = useCallback(
    async (stipulationId: string, documentId: string) => {
      let docToDownloadIndex = -1;
      const docToDownload = formProps.values[`stipulation-${stipulationId}`]?.find((record, idx) => {
        if (record.id === documentId) {
          docToDownloadIndex = idx;
          return true;
        }
        return false;
      });

      // eslint-disable-next-line no-negated-condition
      if (!docToDownload.base64) {
        const { documents } = await getPendingContractStipulationSingleDocument(contractId, stipulationId, documentId);
        const fileBase64 = documents[0].file.base64;
        await parseAndDownloadBase64FileString(docToDownload.name, docToDownload.mimeType, fileBase64);
        const valuesToUpdate = formProps.values[`stipulation-${stipulationId}`];
        valuesToUpdate[docToDownloadIndex] = { ...docToDownload, base64: fileBase64 };

        setFieldValue([`stipulation-${stipulationId}`], valuesToUpdate);
      } else {
        await parseAndDownloadBase64FileString(docToDownload.name, docToDownload.mimeType, docToDownload.base64);
      }
    },
    [contractId, formProps.values, setFieldValue]
  );

  const handleFileFieldData = useCallback(
    async (file, stipulationId, documentId) => {
      try {
        const base64 = await getBase64(file);
        const fileRecord = {
          name: file.name,
          mimeType: file.type,
          base64,
          tags: [`stipulation-${stipulationId}`, `document-${documentId}`],
          id: documentId,
          file,
        };
        setFieldValue(`stipulation-${stipulationId}`, [fileRecord]);
      } catch (error: any) {
        navigate.error();
        throw new Error(error);
      }
    },
    [navigate, setFieldValue]
  );

  const handleFileUpload = async (e, stipulation, onFileUploadProgress) => {
    setLoadingStipulation(stipulation.stipulationId);
    const file = e.target.files[0];

    // Validate file format before upload
    try {
      await subPagePaymentValidationSchema.validate({
        [`stipulation-${stipulation.stipulationId}`]: [
          {
            mimeType: file.type,
          },
        ],
      });
    } catch (error: any) {
      const { errors } = error;
      formProps.setFieldError(`stipulation-${stipulation.stipulationId}`, errors[0]);
      setLoadingStipulation('');
      return;
    }
    try {
      const base64 = await getBase64(file);
      const filePayload = {
        documents: [
          {
            description: file.name,
            file: {
              base64,
            },
          },
        ],
      };
      // Document deleting should be used only if single record is supported, needs to be refactored if we will support more than one document per stipulation
      const existingStipulationFiles = formProps.values[`stipulation-${stipulation.stipulationId}`];
      if (existingStipulationFiles?.length) {
        await deleteStipulationDocument(stipulation.stipulationId, existingStipulationFiles[0].id);
      }

      const response = await uploadPendingContractStipulationDocuments(
        contractId,
        stipulation.stipulationId,
        filePayload,
        onFileUploadProgress
      );

      const { stipulationId, documentId } = response.documents[0];

      handleFileFieldData(file, stipulationId, documentId);
      setLoadingStipulation('');
    } catch (error) {
      setLoadingStipulation('');
    }
  };

  useEffect(() => {
    getStipulations();
  }, [contractId, getStipulations]);

  useEffect(() => {
    if (stipulations.length) {
      getStipulationsDocuments();
    }
  }, [contractId, stipulations, getStipulationsDocuments]);

  // Initialize dynamic stipulation fields to enable proper validation
  useEffect(() => {
    if (!stipulations.length) return;

    stipulations.forEach((stipulation) => {
      const fieldName = `stipulation-${stipulation.stipulationId}`;
      if (!formProps.values[fieldName]) {
        setFieldValue(fieldName, []);
      }
    });

    formProps.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stipulations, formProps.values]);

  return (
    <>
      {stipulations.map((stipulation) => (
        <Layout.Item
          key={`stipulation-${stipulation.stipulationId}`}
          id={`stipulation-${stipulation.stipulationId}`}
          className="u-text-center u-mb"
        >
          <FileUpload
            key={`stipulation-${stipulation.stipulationId}`}
            name={`stipulation-${stipulation.stipulationId}`}
            descriptionText={stipulation.stipulationTypeTranslation || stipulation.stipulationName}
            loading={loadingStipulation === stipulation.stipulationId}
            descriptionSupplementaryText={t('dashboard:popup:uploadSupplementaryText')}
            buttonText={t('dashboard:popup:documentUploadButtonTitle')}
            error={formProps?.errors[`stipulation-${stipulation.stipulationId}`]}
            canDelete={stipulation.stipulationStatus !== 'ACTIVE'}
            onFileRemove={(docId) => deleteStipulationDocument(stipulation.stipulationId || '', docId!)}
            onFileDownload={(docId) => downloadStipulationDocument(stipulation.stipulationId || '', docId!)}
            onChange={(event, onFileUploadProgress) => {
              handleFileUpload(event, stipulation, onFileUploadProgress);
            }}
          />
        </Layout.Item>
      ))}
    </>
  );
};
